import React, { useState } from 'react';
import './AnalyticsDashboard.css';

interface AnalyticsData {
  events: any[];
  ipStats: Record<string, any>;
  summary: {
    totalClicks: number;
    byLanguage: Record<string, number>;
    byDate: Record<string, number>;
  };
}

const AnalyticsDashboard: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('summary');

  // Admin key for authentication
  const adminKey = 'ai-academy-admin';

  // Fetch analytics data from server
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/tracking/data?key=${adminKey}`);

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to fetch analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle authentication
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === adminKey) {
      setIsAuthenticated(true);
      setError('');
      fetchData();
    } else {
      setError('Invalid admin password');
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Render summary tab
  const renderSummary = () => {
    if (!data) return <div className="loading">No data available</div>;

    const { summary } = data;

    // Prepare date data for chart
    const dateLabels = Object.keys(summary.byDate).sort();
    const dateValues = dateLabels.map(date => summary.byDate[date]);

    return (
      <div className="summary-tab">
        <div className="stats-cards">
          <div className="stat-card">
            <h3>Total Clicks</h3>
            <div className="stat-value">{summary.totalClicks}</div>
          </div>

          <div className="stat-card">
            <h3>By Language</h3>
            <div className="language-stats">
              <div className="language-stat">
                <span className="language-label">Arabic:</span>
                <span className="language-value">{summary.byLanguage.ar || 0}</span>
              </div>
              <div className="language-stat">
                <span className="language-label">English:</span>
                <span className="language-value">{summary.byLanguage.en || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="chart-container">
          <h3>Clicks by Date</h3>
          <div className="date-chart">
            {dateLabels.map((date, index) => (
              <div className="chart-bar" key={date}>
                <div className="chart-bar-value" style={{
                  height: `${Math.max(20, (summary.byDate[date] / Math.max(...dateValues)) * 200)}px`
                }}>
                  {summary.byDate[date]}
                </div>
                <div className="chart-bar-label">{date}</div>
              </div>
            ))}
            {dateLabels.length === 0 && <p>No date data available</p>}
          </div>
        </div>
      </div>
    );
  };

  // Render events tab
  const renderEvents = () => {
    if (!data) return <div className="loading">No data available</div>;

    return (
      <div className="events-tab">
        <h3>Recent Events</h3>
        <div className="events-table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date & Time</th>
                <th>IP Address</th>
                <th>Language</th>
                <th>Screen Width</th>
              </tr>
            </thead>
            <tbody>
              {data.events.slice().reverse().map((event, index) => (
                <tr key={index}>
                  <td>{data.events.length - index}</td>
                  <td>{formatDate(event.timestamp)}</td>
                  <td>{event.ip || 'N/A'}</td>
                  <td>{event.language || 'N/A'}</td>
                  <td>{event.screenWidth || 'N/A'}</td>
                </tr>
              ))}
              {data.events.length === 0 && (
                <tr>
                  <td colSpan={5} className="no-data">No events recorded yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render visitors tab
  const renderVisitors = () => {
    if (!data) return <div className="loading">No data available</div>;

    const ipAddresses = Object.keys(data.ipStats);

    return (
      <div className="visitors-tab">
        <h3>Unique Visitors</h3>
        <div className="visitors-count">Total: {ipAddresses.length}</div>

        <div className="visitors-table-container">
          <table className="visitors-table">
            <thead>
              <tr>
                <th>IP Address</th>
                <th>First Seen</th>
                <th>Last Seen</th>
                <th>Clicks</th>
                <th>Languages</th>
              </tr>
            </thead>
            <tbody>
              {ipAddresses.map(ip => {
                const visitor = data.ipStats[ip];
                const languages = Object.keys(visitor.languages || {})
                  .map(lang => `${lang} (${visitor.languages[lang]})`)
                  .join(', ');

                return (
                  <tr key={ip}>
                    <td>{ip}</td>
                    <td>{formatDate(visitor.firstSeen)}</td>
                    <td>{formatDate(visitor.lastSeen)}</td>
                    <td>{visitor.clickCount}</td>
                    <td>{languages || 'N/A'}</td>
                  </tr>
                );
              })}
              {ipAddresses.length === 0 && (
                <tr>
                  <td colSpan={5} className="no-data">No visitors recorded yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return renderSummary();
      case 'events':
        return renderEvents();
      case 'visitors':
        return renderVisitors();
      default:
        return renderSummary();
    }
  };

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <div className="analytics-login">
        <h2>Workshop Analytics Dashboard</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="password">Admin Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter admin password"
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button type="submit" className="login-button">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h2>Workshop Announcement Analytics</h2>
        <div className="header-actions">
          <button onClick={fetchData} className="refresh-button" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
          <button onClick={() => setIsAuthenticated(false)} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button
          className={`tab-button ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={`tab-button ${activeTab === 'visitors' ? 'active' : ''}`}
          onClick={() => setActiveTab('visitors')}
        >
          Visitors
        </button>
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading">Loading analytics data...</div>
        ) : (
          renderTabContent()
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;